import { ProductionTraits, InstrumentForgeProfile } from '../types/instrumentForge.types';

export const HIGH_TRAIT_KEYWORDS: Record<keyof ProductionTraits, string[]> = {
  weight: ['massive', 'thunderous', 'crushing', 'heavy', 'huge', 'sub', 'doom', 'sludge', 'powerful', 'thick', 'deep', 'wall', 'enormous', 'devastating'],
  clarity: ['pristine', 'crystal', 'clean', 'articulate', 'defined', 'tight', 'punchy', 'crisp', 'polished', 'produced', 'clear', 'present'],
  saturation: ['fuzz', 'distortion', 'saturated', 'overdriven', 'cranked', 'smashed', 'destroyed', 'buzzsaw', 'grinding', 'heavy', 'massive'],
  roomSize: ['barn', 'cathedral', 'stadium', 'arena', 'cavernous', 'huge', 'massive verb', 'gated', 'large live', 'warehouse', 'canyon'],
  tightness: ['locked', 'tight', 'precise', 'triggered', 'controlled', 'punchy', 'snappy', 'relentless', 'driving', 'machine-like'],
  warmth: ['tube', 'analog', 'warm', 'vintage', 'tape', 'woolly', 'round', 'natural', 'organic', 'smooth'],
  aggression: ['aggressive', 'crushing', 'devastating', 'violent', 'relentless', 'powerful', 'screaming', 'shouty', 'intense', 'fierce'],
  vintage: ['60s', '70s', '80s', 'vintage', 'classic', 'Plexi', 'Capricorn', 'Muscle Shoals', 'analog', 'retro', 'old-school']
};

export const LOW_TRAIT_KEYWORDS: Record<keyof ProductionTraits, string[]> = {
  weight: ['light', 'thin', 'bright', 'airy', 'delicate', 'sparse', 'minimal'],
  clarity: ['muddy', 'murky', 'dark', 'lo-fi', 'raw', 'rough', 'hazy', 'fuzzy'],
  saturation: ['clean', 'pristine', 'headroom', 'subtle', 'gentle', 'slight'],
  roomSize: ['dead', 'tight', 'minimal', 'dry', 'intimate', 'close', 'small'],
  tightness: ['loose', 'slack', 'swing', 'shuffle', 'behind', 'laid-back', 'sloppy'],
  warmth: ['cold', 'digital', 'sterile', 'clinical', 'modern', 'harsh'],
  aggression: ['gentle', 'soft', 'delicate', 'intimate', 'subtle', 'mellow', 'calm'],
  vintage: ['modern', '2000s', '2010s', '2020s', 'contemporary', 'current', 'new']
};

export const DEFAULT_TRAITS: ProductionTraits = {
  weight: 5, clarity: 5, saturation: 5, roomSize: 5,
  tightness: 5, warmth: 5, aggression: 5, vintage: 5
};

export function extractTraitsFromProfile(profile: InstrumentForgeProfile): ProductionTraits {
  const allText = [
    profile.description,
    profile.identity.era,
    profile.identity.movement,
    profile.identity.tone,
    profile.identity.attitude,
    profile.excludeStyles,
    profile.rhythmSection.drums.kit,
    profile.rhythmSection.drums.room,
    profile.rhythmSection.drums.character,
    profile.rhythmSection.drums.compression,
    profile.rhythmSection.bass.instrument,
    profile.rhythmSection.bass.lock,
    profile.rhythmSection.bass.character,
    profile.guitars.rhythmLeft.character,
    profile.guitars.rhythmRight.character,
    profile.guitars.lead?.character || '',
    profile.guitars.texture?.character || '',
    profile.keysAtmosphere?.character || '',
    profile.mixTargets.map(t => t.goal + ' ' + t.details).join(' ')
  ].join(' ').toLowerCase();
  
  const traits: ProductionTraits = { ...DEFAULT_TRAITS };
  
  (Object.keys(traits) as Array<keyof ProductionTraits>).forEach(traitKey => {
    let score = 5;
    HIGH_TRAIT_KEYWORDS[traitKey].forEach(kw => {
      if (allText.includes(kw.toLowerCase())) score += 1.5;
    });
    LOW_TRAIT_KEYWORDS[traitKey].forEach(kw => {
      if (allText.includes(kw.toLowerCase())) score -= 1.5;
    });
    if (profile.keywords[traitKey]) {
      profile.keywords[traitKey].forEach(kw => {
        if (allText.includes(kw.toLowerCase())) score += 0.5;
      });
    }
    traits[traitKey] = Math.max(0, Math.min(10, Math.round(score * 10) / 10));
  });
  
  return traits;
}

export function blendTraits(
  traitsA: ProductionTraits,
  traitsB: ProductionTraits,
  ratioA: number
): ProductionTraits {
  const wA = ratioA / 100;
  const wB = 1 - wA;
  
  return {
    weight: Math.round((traitsA.weight * wA + traitsB.weight * wB) * 10) / 10,
    clarity: Math.round((traitsA.clarity * wA + traitsB.clarity * wB) * 10) / 10,
    saturation: Math.round((traitsA.saturation * wA + traitsB.saturation * wB) * 10) / 10,
    roomSize: Math.round((traitsA.roomSize * wA + traitsB.roomSize * wB) * 10) / 10,
    tightness: Math.round((traitsA.tightness * wA + traitsB.tightness * wB) * 10) / 10,
    warmth: Math.round((traitsA.warmth * wA + traitsB.warmth * wB) * 10) / 10,
    aggression: Math.round((traitsA.aggression * wA + traitsB.aggression * wB) * 10) / 10,
    vintage: Math.round((traitsA.vintage * wA + traitsB.vintage * wB) * 10) / 10
  };
}

export const TRAIT_DISPLAY_INFO: Record<keyof ProductionTraits, { label: string; description: string }> = {
  weight: { label: 'Weight', description: 'Low-end presence and overall heaviness' },
  clarity: { label: 'Clarity', description: 'Definition and separation of elements' },
  saturation: { label: 'Saturation', description: 'Amount of distortion/overdrive' },
  roomSize: { label: 'Room Size', description: 'Reverb and ambient space' },
  tightness: { label: 'Tightness', description: 'Rhythmic precision and timing' },
  warmth: { label: 'Warmth', description: 'Analog character and harmonic richness' },
  aggression: { label: 'Aggression', description: 'Attack and intensity level' },
  vintage: { label: 'Vintage', description: 'Era-appropriate production aesthetic' }
};
